import { useState, useEffect, useCallback } from "react";
import { Modal, SelectPicker as RsuiteSelectPicker } from "rsuite";
import { DatePicker, DataInput, Button } from "../../../../components";
import ToastService from "../../../../services/toast.service";
import logger from "../../../../utils/logger";

import "./createWinzEntry.css";

// Constants
const FORM_TYPES = [
  { label: "Regular", value: "Regular" },
  { label: "Arrears", value: "Arrears" },
];

const DEFAULT_FORM_VALUES = {
  status: 100,
  prevWeek: "",
  renewalDate: "",
  errorMessage: "",
  ecetopUpHours: "0",
  currentEcetopUpWeek: "0",
  previousEcetopUpWeek: "0",
};

function CreateWinzEntryModal({ open, onClose, service, emailRecordId }) {
  const resetFormData = useCallback(
    () => ({
      type: "Regular",
      caregiversName: "",
      childName: "",
      ccahours: "",
      currentWeek: "",
      swn: "",
      paymentDate: null,
      periodTo: null,
      renewalDate: null,
      emailRecordId: emailRecordId || "",
    }),
    [emailRecordId]
  );

  const [formData, setFormData] = useState(() => resetFormData());
  const [loading, setLoading] = useState(false);

  // Update emailRecordId when prop changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      emailRecordId: emailRecordId || "",
    }));
  }, [emailRecordId]);

  // Function to format SWN number with dashes, max 9 digits
  const formatSwnNumber = (value) => {
    // Remove all non-numeric characters and limit to 9 digits
    const numbers = value.replace(/\D/g, "").slice(0, 9);
    // Add dashes after every 3 digits
    let formatted = "";
    for (let i = 0; i < numbers.length; i++) {
      if (i > 0 && i % 3 === 0) {
        formatted += "-";
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Function to format currency input
  const formatCurrency = (value) => {
    // Remove all non-numeric and non-decimal characters
    const cleaned = value.replace(/[^\d.]/g, "");
    // Allow only one decimal point
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return parts[0] + "." + parts.slice(1).join("");
    }
    return cleaned;
  };

  const handleInputChange = (field, value) => {
    const actualValue = value.target ? value.target.value : value;

    setFormData((prev) => ({
      ...prev,
      [field]: actualValue,
    }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (field, date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
  };
  // Function to format date to dd/MM/yy format for API
  const formatDateForApi = (date) => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const handleSubmit = async () => {
    try {
      const errors = [];

      if (!formData.type) errors.push("Type");
      if (!formData.caregiversName?.trim()) errors.push("Caregiver Name");
      if (!formData.childName?.trim()) errors.push("Child Name");
      if (!formData.emailRecordId) errors.push("Email Record ID");
      if (!formData.swn) errors.push("SWN Number");
      if (!formData.currentWeek) errors.push("Current Week Amount");
    

      // Optional field validation with better UX
      if (formData.ccahours && (isNaN(formData.ccahours) || Number(formData.ccahours) < 0)) {
        errors.push("CCA Hours (must be a valid positive number)");
      }

      if (errors.length > 0) {
        ToastService.error(`Please fix the following fields: ${errors.join(", ")}`);
        return;
      }

      setLoading(true);

      if (!service) {
        throw new Error("Service instance not provided");
      }

      // Prepare data for API with formatted dates
      const apiData = {
        ...formData,
        paymentDate: formatDateForApi(formData.paymentDate),
        periodTo: formatDateForApi(formData.periodTo),
        renewalDate: formatDateForApi(formData.renewalDate),
        errorCodes: "[]",
      };

      //TODO: do we actually need all this extra data?
      Object.assign(apiData, DEFAULT_FORM_VALUES);

      // Create the WINZ entry
      await service.createWinzEntry(apiData);

      ToastService.success("WINZ entry created successfully");

      // Reset form and close modal
      setFormData(resetFormData());
      onClose();
    } catch (error) {
      logger.error("Error creating WINZ entry:", error);
      const errorArry = error?.response?.data?.errors || [];
      logger.error("Error details:", errorArry);
      if (errorArry.length > 0) {
        ToastService.error(`Failed to create WINZ entry: ${errorArry.join(", ")}`);
      } else if (error.message) {
        ToastService.error(`Failed to create WINZ entry: ${error.message}`);
      } else {
        ToastService.error("Failed to create WINZ entry: Unknown error");
      }
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    // Reset form data
    setFormData(resetFormData());
    onClose();
  };

  return (
    <Modal open={open} onClose={handleCancel} size="lg" data-testid="create-entry-modal">
      <Modal.Header>
        <Modal.Title>Create WINZ Entry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {" "}
        <div className="create-winz-entry-container">
          {" "}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "30px" }}>
            <div className="custom-select-picker-wrapper">
              <label className="custom-select-picker-label">Type*</label>
              <RsuiteSelectPicker
                className="custom-select-picker"
                placeholder="Select Type"
                value={formData.type}
                onChange={(value) => handleSelectChange("type", value)}
                data={FORM_TYPES}
                searchable={false}
                cleanable={false}
                style={{ width: "100%" }}
              />
            </div>
            <DataInput
              label="Child Name*"
              placeholder="Enter Child Name"
              value={formData.childName}
              onChange={(e) => handleInputChange("childName", e)}
            />
          </div>
          <div className="create-winz-entry-double-column">
            <DataInput
              label="Caregiver Name*"
              placeholder="Enter Caregiver Name"
              value={formData.caregiversName}
              onChange={(e) => handleInputChange("caregiversName", e)}
            />
            <DataInput
              label="CCA Hours"
              placeholder="Enter CCA Hours"
              value={formData.ccahours}
              onChange={(e) => handleInputChange("ccahours", e)}
              type="number"
              regex="[0-9]"
            />
          </div>
          <div className="create-winz-entry-double-column">
            <DataInput
              label="Current Week ($)"
              placeholder="Enter Current Week Amount"
              value={formData.currentWeek}
              onChange={(e) => handleInputChange("currentWeek", e)}
              formatter={formatCurrency}
              prefix="$"
            />
            <DataInput
              label="SWN Number"
              placeholder="000-000-000"
              value={formData.swn}
              onChange={(e) => handleInputChange("swn", e)}
              formatter={formatSwnNumber}
              maxLength={11}
            />
          </div>
          <div className="create-winz-entry-triple-column">
            <DatePicker
              label="Payment Date"
              placeholder="Select payment date"
              value={formData.paymentDate}
              onChange={(date) => handleDateChange("paymentDate", date)}
              format="dd/MM/yyyy"
            />
            <DatePicker
              label="Period To"
              placeholder="Select period end date"
              value={formData.periodTo}
              onChange={(date) => handleDateChange("periodTo", date)}
              format="dd/MM/yyyy"
            />
            <DatePicker
              label="Renewal Date"
              placeholder="Select renewal date"
              value={formData.renewalDate}
              onChange={(date) => handleDateChange("renewalDate", date)}
              format="dd/MM/yyyy"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleSubmit} text="Create" loading={loading} disabled={loading} />
        <Button onClick={handleCancel} text="Cancel" color="blue" disabled={loading} />
      </Modal.Footer>
    </Modal>
  );
}

export default CreateWinzEntryModal;
